import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { EMPTY, from, merge } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { AppDatePipe } from 'src/app/infrastructure/pipes/app-date.pipe';
import { BillingDryRunComponent } from '../billing-dry-run/billing-dry-run.component';
import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { ButtonsRowComponent } from 'src/app/features/shared/controls/container/buttons-row/buttons-row.component';
import { IconComponent } from 'src/app/features/shared/icons/container/icon.component';
import { InputControlComponent } from 'src/app/features/shared/controls/container/input-control/input-control.component';
import { buttonsRowAlign } from 'src/app/features/shared/controls/models/buttons-row-options';
import { creditTransactionDto, ICreditTransactionDto } from 'src/assets/common/models/credit-transaction-dto.g';
import { domainArchiveDto } from 'src/assets/common/models/domain-archive-dto.g';
import { domainBillingDetailsDto } from 'src/assets/common/models/domain-billing-details-dto.g';
import { domainCreditsDto, IDomainCreditsDto } from 'src/assets/common/models/domain-credits-dto.g';
import { domainCreditsPurchaseDto } from 'src/assets/common/models/domain-credits-purchase-dto.g';
import { domainCreditsPurchaseResultDto, IDomainCreditsPurchaseResultDto } from 'src/assets/common/models/domain-credits-purchase-result-dto.g';
import { ICreditInvoiceDto } from 'src/assets/common/models/credit-invoice-dto.g';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { unwrapData } from 'src/app/infrastructure/functions/unwrap-data.operator';
import { creditInvoice_print } from '../../models/credit-invoice-print';
import { money_format, number_format, period_label, period_offset } from '../../models/credits-format';

const __typeLabel: Record<string, string> = {
    purchase: 'aankoop',
    monthlyCharge: 'maandafschrijving',
    correction: 'correctie',
    refund: 'terugbetaling',
    startCredits: 'startcredits',
};

const __emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Klantrekening van één domain (bijstelling 2026-10-26 (b), stap 3): tab credits op de domain-pagina én het
// accountoverzicht in Instellingen (ook voor een opgeschort domain). Context = het eigen lidmaatschap (dauGuid).
//   - overzicht: saldo, kredietruimte, kosten deze maand, verwachte afschrijving, dekking en toestand;
//   - credits kopen (DomainCredits/Page/Update) per bundel: online via Mollie (terug op Instellingen ›
//     Credits met ?payment=…), zonder Mollie-sleutel een aanvraag aan de beheerder;
//   - factuurgegevens (btw-nummer, factuur-e-mail) — naam en adres komen van het domain;
//   - archiveren (alleen de owner): alleen-lezen, alleen opslag wordt gerekend;
//   - grootboek met facturen en de kosten per maand (uitleg).
@Component({
    selector: 'app-domain-credits-panel',
    standalone: true,
    imports: [AppDatePipe, BillingDryRunComponent, ButtonActionComponent, ButtonsRowComponent, IconComponent, InputControlComponent],
    templateUrl: './domain-credits-panel.component.html',
    styleUrl: './domain-credits-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DomainCreditsPanelComponent {
    private readonly __apiService = injectRequired(ApiService);
    private readonly __destroyRef = injectRequired(DestroyRef);

    input_dauGuid$$ = input.required<string | null>({ alias: 'dauGuid' });

    out_onPageLoaded = output<void>({ alias: 'onPageLoaded' });
    // Na een wijziging (archief, factuurgegevens): het accountoverzicht kan zijn lijst bijwerken.
    out_changed = output<IDomainCreditsDto>({ alias: 'changed' });

    readonly buttonsRowAlign = buttonsRowAlign;
    readonly creditTransactionDto = creditTransactionDto;
    readonly domainCreditsDto = domainCreditsDto;
    readonly money_format = money_format;
    readonly number_format = number_format;
    readonly period_label = period_label;
    type_label = (type: string | null) => __typeLabel[type ?? ''] ?? type ?? '';

    private readonly __credits$$ = signal<IDomainCreditsDto | null>(null);
    readonly credits$$ = this.__credits$$.asReadonly();
    private readonly __loadFailed$$ = signal(false);
    readonly loadFailed$$ = this.__loadFailed$$.asReadonly();
    private readonly __transactions$$ = signal<ICreditTransactionDto[] | null>(null);
    readonly transactions$$ = this.__transactions$$.asReadonly();
    private readonly __saving$$ = signal(false);
    readonly saving$$ = this.__saving$$.asReadonly();
    private readonly __requestSent$$ = signal<number | null>(null);
    readonly requestSent$$ = this.__requestSent$$.asReadonly();
    private readonly __changeTick$$ = signal(0);

    // Aantal bundels (BillingSettings.PurchaseBundleCredits credits per bundel).
    readonly control_bundles = new FormControl<string | null>('1', [Validators.required, Validators.pattern(/^[1-9]\d{0,3}$/)]);
    readonly control_note = new FormControl<string | null>(null, Validators.maxLength(1000));
    readonly control_vatNumber = new FormControl<string | null>(null, Validators.maxLength(32));
    readonly control_billingEmail = new FormControl<string | null>(null, [Validators.maxLength(256), Validators.pattern(__emailPattern)]);

    readonly canPurchase$$ = computed(() => !!this.credits$$()?.[domainCreditsDto.canPurchase]);
    readonly isOwner$$ = computed(() => !!this.credits$$()?.[domainCreditsDto.isOwner]);
    readonly state$$ = computed(() => this.credits$$()?.[domainCreditsDto.billingState] ?? 'active');
    readonly chargeLabel$$ = computed(() => period_label(period_offset(1)));
    readonly bundle$$ = computed(() => Math.max(1, this.credits$$()?.[domainCreditsDto.purchaseBundleCredits] ?? 1));
    readonly paymentOnline$$ = computed(() => !!this.credits$$()?.[domainCreditsDto.paymentOnline]);
    readonly purchaseCredits$$ = computed(() => {
        this.__changeTick$$();
        return this.control_bundles.invalid ? null : Number(this.control_bundles.value) * this.bundle$$();
    });

    // Prijs van de aanvraag inclusief btw (indicatie; de factuur gebruikt de prijs op het moment van boeken).
    readonly purchaseTotal$$ = computed(() => {
        this.__changeTick$$();
        const __c = this.credits$$();
        const __credits = this.purchaseCredits$$();
        if (!__c || __credits == null) return null;
        const __amount = __credits * (__c[domainCreditsDto.creditPrice] ?? 0);
        return Math.round(__amount * (1 + (__c[domainCreditsDto.vatPerc] ?? 0) / 100) * 100) / 100;
    });
    readonly purchaseDisabled$$ = computed(() => {
        this.__changeTick$$();
        return this.saving$$() || this.control_bundles.invalid || this.control_note.invalid;
    });
    readonly detailsDisabled$$ = computed(() => {
        this.__changeTick$$();
        const __c = this.credits$$();
        return this.saving$$() || this.control_vatNumber.invalid || this.control_billingEmail.invalid
            || ((this.control_vatNumber.value ?? '').trim() === (__c?.[domainCreditsDto.vatNumber] ?? '')
                && (this.control_billingEmail.value ?? '').trim() === (__c?.[domainCreditsDto.billingEmail] ?? ''));
    });

    protected readonly _text = {
        overview: 'saldo',
        balance: 'saldo',
        limit: 'kredietruimte',
        spent: 'kosten deze maand tot nu',
        expected: 'verwachte afschrijving',
        required: 'vereist saldo (deze en volgende maand)',
        available: 'beschikbaar',
        availableInfo: 'is het beschikbare saldo negatief, dan worden nieuwe leden, functies en uploads geweigerd tot er is opgewaardeerd.',
        notStarted: 'de afrekening is nog niet gestart: er wordt nog niets afgeschreven.',
        readOnly: 'dit domain is alleen-lezen door een tekort aan credits. Waardeer op om het weer volledig te gebruiken.',
        suspended: 'dit domain is opgeschort door een tekort aan credits en is niet bereikbaar. Waardeer op om het te herstellen.',
        cleanupFrom: 'de gegevens mogen worden opgeruimd vanaf',
        graceUntil: 'er is een achterstand; waardeer op vóór',
        archived: 'dit domain is gearchiveerd: alleen lezen, alleen de opslag wordt gerekend.',
        purchase: 'credits kopen',
        purchaseInfo: 'je aanvraag gaat naar de beheerder. Na ontvangst van de betaling worden de credits bijgeboekt en ontvang je de factuur.',
        purchaseInfoOnline: 'je betaalt via Mollie (iDEAL, creditcard en meer). Direct na de betaling staan de credits op het saldo en is de factuur beschikbaar in het grootboek.',
        bundles: 'aantal bundels',
        bundleOf: 'bundel van',
        bundlesInvalid: 'een geheel getal vanaf 1',
        credits: 'credits',
        note: 'toelichting (optioneel)',
        total: 'te betalen inclusief btw',
        request: 'aanvraag versturen',
        pay: 'betalen',
        requestSent: 'aanvraag verstuurd voor',
        billingDetails: 'factuurgegevens',
        billingDetailsInfo: 'naam en adres komen van het domain (wijzigen via domain bewerken).',
        vatNumber: 'btw-nummer',
        billingEmail: 'e-mail voor facturen en meldingen',
        billingEmailInvalid: 'ongeldig e-mailadres',
        billingEmailPlaceholder: 'leeg = e-mailadres van de owner',
        save: 'opslaan',
        archive: 'archief',
        archiveInfo: 'een gearchiveerd domain is voor iedereen alleen-lezen; alleen de opslag wordt nog gerekend. Opheffen kan zodra het saldo de kosten dekt.',
        archiveSet: 'domain archiveren',
        archiveClear: 'archief opheffen',
        ledger: 'grootboek',
        date: 'datum',
        type: 'soort',
        amount: 'credits',
        invoice: 'factuur',
        invoicePrint: 'factuur bekijken / afdrukken',
        empty: 'nog geen boekingen',
        loading: 'laden…',
        loadFailed: 'kon de klantrekening niet laden',
    };

    constructor() {
        merge(this.control_bundles.valueChanges, this.control_note.valueChanges, this.control_vatNumber.valueChanges, this.control_billingEmail.valueChanges)
            .pipe(takeUntilDestroyed(this.__destroyRef))
            .subscribe(() => this.__changeTick$$.update(v => v + 1));

        effect(() => {
            const __dauGuid = this.input_dauGuid$$();
            untracked(() => this.__load(__dauGuid));
        });
    }

    emit_purchase(): void {
        if (this.purchaseDisabled$$()) return;
        const __credits = this.purchaseCredits$$();
        if (__credits == null) return;
        const __body = {
            [domainCreditsPurchaseDto.credits]: __credits,
            [domainCreditsPurchaseDto.note]: this.control_note.value?.trim() || null,
        };
        // Online: door naar de betaalpagina van Mollie (de app wordt verlaten). Anders: aanvraag verstuurd.
        this.__saving$$.set(true);
        from(this.__apiService.POST('/api/DomainCredits/purchase', { ...this.__context(), body: __body as any } as any)).pipe(
            unwrapData(),
            catchError(() => { this.__saving$$.set(false); return EMPTY; }),
        ).subscribe(data => {
            const __result = ((data as any)?.modelDto ?? null) as IDomainCreditsPurchaseResultDto | null;
            const __checkoutUrl = __result?.[domainCreditsPurchaseResultDto.checkoutUrl];
            if (__checkoutUrl) {
                window.location.href = __checkoutUrl;
                return;
            }
            this.__saving$$.set(false);
            this.__requestSent$$.set(__credits);
            this.control_bundles.setValue('1', { emitEvent: false });
            this.control_note.reset(null, { emitEvent: false });
            this.__changeTick$$.update(v => v + 1);
        });
    }

    emit_detailsSave(): void {
        if (this.detailsDisabled$$()) return;
        const __body = {
            [domainBillingDetailsDto.vatNumber]: this.control_vatNumber.value?.trim() || null,
            [domainBillingDetailsDto.billingEmail]: this.control_billingEmail.value?.trim() || null,
        };
        this.__request(this.__apiService.PATCH('/api/DomainCredits/billing-details', { ...this.__context(), body: __body as any } as any));
    }

    emit_archive(archived: boolean): void {
        if (this.saving$$()) return;
        this.__request(this.__apiService.PATCH('/api/DomainCredits/archive', { ...this.__context(), body: { [domainArchiveDto.archived]: archived } as any } as any));
    }

    emit_invoicePrint(transaction: ICreditTransactionDto): void {
        const __guid = transaction[creditTransactionDto.invoiceGuid];
        if (!__guid) return;
        from(this.__apiService.GET('/api/DomainCredits/invoice', {
            params: { query: { domainAUserGuid: this.input_dauGuid$$() ?? undefined, key: __guid } },
        } as any)).pipe(
            unwrapData(),
            catchError(() => EMPTY),
        ).subscribe(data => {
            const __invoice = (data as any)?.modelDto as ICreditInvoiceDto | undefined;
            if (__invoice) creditInvoice_print(__invoice);
        });
    }

    private __context() {
        return { params: { query: { domainAUserGuid: this.input_dauGuid$$() ?? undefined } } };
    }

    // Wijziging: het antwoord is de bijgewerkte klantrekening.
    private __request(request: Promise<unknown>, onDone?: () => void): void {
        this.__saving$$.set(true);
        from(request).pipe(
            unwrapData(),
            catchError(() => EMPTY),
            finalize(() => this.__saving$$.set(false)),
        ).subscribe(data => {
            const __credits = ((data as any)?.modelDto ?? null) as IDomainCreditsDto | null;
            if (__credits) {
                this.__credits_set(__credits);
                this.out_changed.emit(__credits);
            }
            onDone?.();
            this.__changeTick$$.update(v => v + 1);
        });
    }

    private __load(dauGuid: string | null): void {
        this.__credits$$.set(null);
        this.__transactions$$.set(null);
        this.__loadFailed$$.set(false);
        this.__requestSent$$.set(null);
        if (!dauGuid) return;

        const __context = { params: { query: { domainAUserGuid: dauGuid } } } as any;
        from(this.__apiService.GET('/api/DomainCredits/details', __context)).pipe(
            unwrapData(),
            catchError(() => { this.__loadFailed$$.set(true); return EMPTY; }),
            finalize(() => setTimeout(() => this.out_onPageLoaded.emit(), 0)),
        ).subscribe(data => {
            if (dauGuid === this.input_dauGuid$$()) this.__credits_set(((data as any)?.modelDto ?? null) as IDomainCreditsDto | null);
        });
        from(this.__apiService.GET('/api/DomainCredits/transactions', __context)).pipe(
            unwrapData(),
            catchError(() => { this.__transactions$$.set([]); return EMPTY; }),
        ).subscribe(data => {
            if (dauGuid === this.input_dauGuid$$()) this.__transactions$$.set(((data as any)?.list ?? []) as ICreditTransactionDto[]);
        });
    }

    private __credits_set(credits: IDomainCreditsDto | null): void {
        this.__credits$$.set(credits);
        this.control_vatNumber.setValue(credits?.[domainCreditsDto.vatNumber] ?? null, { emitEvent: false });
        this.control_billingEmail.setValue(credits?.[domainCreditsDto.billingEmail] ?? null, { emitEvent: false });
        for (const __control of [this.control_bundles, this.control_note, this.control_vatNumber, this.control_billingEmail]) {
            if (credits?.[domainCreditsDto.canPurchase]) __control.enable({ emitEvent: false });
            else __control.disable({ emitEvent: false });
        }
        this.__changeTick$$.update(v => v + 1);
    }
}
